
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import type { Company, CompanyState } from '../types';
import { apiService } from '../services/api.services';
import { mapValidEstipulanteBusinessLinks } from '../utils/estipulanteLegacyKey';
import { resolveEffectiveRole } from '../utils/resolveEffectiveRole';
import { useVidaProtocolAtendimentoStore } from './vida/vidaProtocolServiceStore';

export const SELECTED_VINCULO_STORAGE_KEY = 'company.selectedVinculoId';

const persistSelectedVinculoId = (id: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!id) {
    window.localStorage.removeItem(SELECTED_VINCULO_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(SELECTED_VINCULO_STORAGE_KEY, id);
};

export const getPersistedSelectedVinculoId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(SELECTED_VINCULO_STORAGE_KEY);
};



export const useCompanyStore = create<CompanyState>()(
  subscribeWithSelector(
    (set, get) => ({
      selectedCompany: null,
      vinculos: [],
      selectedVinculo: null,
      sessionRole: null,
      companyCardOpenSignal: 0,

      getRoleDisplay: () => {
        const state = get();
        return resolveEffectiveRole(state);
      },

      hasRole: (role: string) => {
        const currentRole = get().getRoleDisplay();
        return currentRole === role.trim().toUpperCase();
      },

      isAdmin: () => get().hasRole('BACKOFFICE'),
      isUser: () => get().hasRole('ESTIPULANTE') || get().hasRole('SUB-ESTIPULANTE'),
      isCorretor: () => get().hasRole('CORRETOR'),

      setSelectedCompany: (company: Company | null) => {
        set({ selectedCompany: company });
      },

      setVinculos: (vinculos) => {
        set({ vinculos });
      },

      loadVinculosByPessoaId: async (idPessoa: string) => {
        try {
          const vinculosResponse = await apiService.login.postListBusinessLinksByPessoaId(idPessoa);
          const validBusinessLinks = mapValidEstipulanteBusinessLinks(vinculosResponse?.vinculos ?? []);
          set({ vinculos: validBusinessLinks });
          return true;
        } catch {
          set({ vinculos: [] });
          return false;
        }
      },

      setSelectedVinculo: (selectedVinculo) => {
        persistSelectedVinculoId(selectedVinculo?.idVinculoNegocio ?? null);
        set({ selectedVinculo });
      },

      setSessionRole: (sessionRole) => {
        set({ sessionRole });
      },

      requestCompanyCardOpen: () => {
        set((state) => ({ companyCardOpenSignal: state.companyCardOpenSignal + 1 }));
      },

      clearSelectedCompany: () => {
        set({ selectedCompany: null });
      },

      clearCompanySession: () => {
        useVidaProtocolAtendimentoStore.getState().clear();
        persistSelectedVinculoId(null);
        set({
          selectedCompany: null,
          vinculos: [],
          selectedVinculo: null,
          sessionRole: null,
          companyCardOpenSignal: 0,
        });
      },
    })
  )
);

export const useRoleDisplay = () => useCompanyStore((state) => state.getRoleDisplay());
export const useIsAdmin = () => useCompanyStore((state) => state.isAdmin());
export const useIsUser = () => useCompanyStore((state) => state.isUser());
export const useIsCorretor = () => useCompanyStore((state) => state.isCorretor());
